// SplitTaskModal: bulk-create N child tasks under a parent. Each row is
// one new subtask with title + R (responsible) assignee. The new tasks
// inherit the parent's priority, category, and due date unless overridden
// later. Created with parent_id = parent.id, so they appear in the
// parent's project view automatically.
//
// Use case: Regina creates "母亲节大促" as a parent, then opens the split
// modal and adds 7 sub-tasks for menu/poster/training/etc, each routed
// to the right store manager in one batch instead of 7 separate clicks.

import { useState } from 'react';
import { Icon } from './Icon.jsx';

const blankRow = (defaultStaffId) => ({
  title: '',
  staffId: defaultStaffId || ''
});

export const SplitTaskModal = ({ parent, staff, onClose, onCreate }) => {
  const activeStaff = staff.filter(s => s.active);
  const defaultStaffId = activeStaff.find(s => s.role !== 'admin')?.id || '';
  const [rows, setRows] = useState(() => [blankRow(defaultStaffId), blankRow(defaultStaffId)]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  function setRow(i, patch) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function addRow() { setRows(rs => [...rs, blankRow(defaultStaffId)]); }
  function removeRow(i) {
    setRows(rs => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);
  }

  async function submit(e) {
    e.preventDefault();
    setErr('');
    const valid = rows.filter(r => r.title.trim() && r.staffId);
    if (valid.length === 0) {
      return setErr('请至少填写一条子项');
    }
    setBusy(true);
    try {
      // Bulk-create: one POST per row, with parent_id wired to the parent.
      // The Worker generates sequential IDs; ordering is preserved by
      // submitting sequentially. Errors on row N stop the batch.
      for (const r of valid) {
        await onCreate({
          title: r.title.trim(),
          detail: '',
          category: parent.category || '门店运营',
          priority: parent.priority || 'P1',
          dueDate: parent.dueDate || '',
          recurring: '一次性',
          parentId: parent.id,
          owners: [{ staffId: r.staffId, role: 'R', isPrimary: true }]
        });
      }
      onClose();
    } catch (e) {
      setErr(e?.message || '创建失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-labelledby="split-task-title"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <h2 className="modal-title" id="split-task-title">拆分待办</h2>
        <div style={{ marginBottom: 14, padding: 12, background: 'var(--vava-bg)', borderRadius: 6, fontSize: 13 }}>
          <div style={{ color: 'var(--vava-text-muted)', fontSize: 11 }}>所属待办</div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>{parent.title}</div>
          <div style={{ color: 'var(--vava-text-muted)', fontSize: 11, marginTop: 4 }}>
            子项继承：{parent.category || '—'} · {parent.priority} · 截止 {parent.dueDate || '—'}
          </div>
        </div>

        <form onSubmit={submit}>
          <div style={{ marginBottom: 12 }}>
            {rows.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 200px 32px', gap: 8, marginBottom: 8 }}>
                <input className="vava-input" placeholder={`子项 ${i + 1} 标题`}
                       value={r.title}
                       onChange={(e) => setRow(i, { title: e.target.value })}/>
                <select className="vava-select" value={r.staffId}
                        onChange={(e) => setRow(i, { staffId: e.target.value })}>
                  <option value="">选择负责人...</option>
                  {activeStaff.map(s => (
                    <option key={s.id} value={s.id}>{s.name} · {s.title}</option>
                  ))}
                </select>
                <button type="button" className="raci-add-btn" style={{ padding: '0 8px' }}
                        onClick={() => removeRow(i)}
                        disabled={rows.length <= 1}
                        title="删除这行" aria-label="删除子项行">
                  <Icon name="x" className="icon-sm"/>
                </button>
              </div>
            ))}
          </div>

          <button type="button" className="raci-add-btn" onClick={addRow}>
            <Icon name="plus" className="icon-sm"/> 加子项
          </button>

          {err && <div className="login-error" style={{ marginTop: 12 }}>⚠ {err}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>取消</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              <Icon name="plus"/>{busy ? '创建中…' : `创建 ${rows.filter(r => r.title.trim() && r.staffId).length} 个子项`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
